import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { employeesAPI, Employee } from '../../services/api'

export interface EmployeesState {
  employees: Employee[]
  currentEmployee: Employee | null
  total: number
  isLoading: boolean
  error: string | null
  filters: {
    department?: string
    status?: string
    search?: string
  }
  pagination: {
    page: number
    limit: number
  }
}

const initialState: EmployeesState = {
  employees: [],
  currentEmployee: null,
  total: 0,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
  },
}

// Async thunks
export const fetchEmployees = createAsyncThunk(
  'employees/fetchEmployees',
  async (params: {
    skip?: number
    limit?: number
    department?: string
    status?: string
    search?: string
  } = {}, { rejectWithValue }: any) => {
    try {
      const response = await employeesAPI.getEmployees(params)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch employees')
    }
  }
)

export const fetchEmployee = createAsyncThunk(
  'employees/fetchEmployee',
  async (employeeId: number, { rejectWithValue }) => {
    try {
      const response = await employeesAPI.getEmployee(employeeId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch employee')
    }
  }
)

export const createEmployee = createAsyncThunk(
  'employees/createEmployee',
  async (employeeData: Partial<Employee>, { rejectWithValue }) => {
    try {
      const response = await employeesAPI.createEmployee(employeeData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create employee')
    }
  }
)

export const updateEmployee = createAsyncThunk(
  'employees/updateEmployee',
  async ({ employeeId, employeeData }: { employeeId: number; employeeData: Partial<Employee> }, { rejectWithValue }) => {
    try {
      const response = await employeesAPI.updateEmployee(employeeId, employeeData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update employee')
    }
  }
)

export const deleteEmployee = createAsyncThunk(
  'employees/deleteEmployee',
  async (employeeId: number, { rejectWithValue }) => {
    try {
      await employeesAPI.deleteEmployee(employeeId)
      return employeeId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete employee')
    }
  }
)

const employeesSlice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<EmployeesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<EmployeesState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentEmployee: (state) => {
      state.currentEmployee = null
    },
    // Real-time updates
    addEmployee: (state, action: PayloadAction<Employee>) => {
      state.employees.unshift(action.payload)
      state.total += 1
    },
    updateEmployeeInList: (state, action: PayloadAction<Employee>) => {
      const index = state.employees.findIndex(employee => employee.employee_id === action.payload.employee_id)
      if (index !== -1) {
        state.employees[index] = action.payload
      }
      if (state.currentEmployee?.employee_id === action.payload.employee_id) {
        state.currentEmployee = action.payload
      }
    },
    removeEmployeeFromList: (state, action: PayloadAction<string>) => {
      state.employees = state.employees.filter(employee => employee.employee_id !== action.payload)
      state.total -= 1
      if (state.currentEmployee?.employee_id === action.payload) {
        state.currentEmployee = null
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch employees
      .addCase(fetchEmployees.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.isLoading = false
        state.employees = action.payload.employees
        state.total = action.payload.total
        state.error = null
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        // Ensure employees array is never undefined
        if (!state.employees) {
          state.employees = []
        }
      })
      // Fetch single employee
      .addCase(fetchEmployee.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentEmployee = action.payload
        state.error = null
      })
      .addCase(fetchEmployee.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Create employee
      .addCase(createEmployee.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.isLoading = false
        state.employees.unshift(action.payload)
        state.total += 1
        state.error = null
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update employee
      .addCase(updateEmployee.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.employees.findIndex(employee => employee.employee_id === action.payload.employee_id)
        if (index !== -1) {
          state.employees[index] = action.payload
        }
        if (state.currentEmployee?.employee_id === action.payload.employee_id) {
          state.currentEmployee = action.payload
        }
        state.error = null
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete employee
      .addCase(deleteEmployee.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.isLoading = false
        state.employees = state.employees.filter(employee => employee.id !== action.payload)
        state.total -= 1
        if (state.currentEmployee?.id === action.payload) {
          state.currentEmployee = null
        }
        state.error = null
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setFilters,
  clearFilters,
  setPagination,
  clearError,
  clearCurrentEmployee,
  addEmployee,
  updateEmployeeInList,
  removeEmployeeFromList,
} = employeesSlice.actions

export default employeesSlice.reducer
